import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { parse as parseQueryString, stringify as stringifyQueryString } from 'query-string';
import * as React from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { IAccount, IChargifyInvoice } from '../../../../extViewmodels';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { numberToUSDCurrencyStringValue } from '../../../../models/UiUtils';
import { useAdminAccountExportOpenInvoicesMutation, useAdminAccountOpenInvoicesQuery } from '../../../../queries';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { Button } from '../../../../web/components/Button';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../web/components/DataGrid';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { ExportConfirmationModalV2 } from '../../../../web/components/ExportConfirmation';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../../web/components/Portal';
import { EmptySearchIcon } from '../../../../web/components/svgs/icons/EmptySearchIcon';
import { ExportIcon } from '../../../../web/components/svgs/icons/ExportIcon';
import { styleSheet as dbStyleSheet } from '../../../../web/containers/dataBoards/styles';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

enum InvoiceRoleOption {
	All = 'All',
	Adhoc = 'Adhoc',
	Renewal = 'Renewal',
	Signup = 'Signup',
}

const InvoiceRoleOptions = Object.keys(InvoiceRoleOption).map(key => {
	const option: ISelectOption = {
		id: key,
		text: InvoiceRoleOption[key as keyof typeof InvoiceRoleOption],
		dataContext: key,
	};
	return option;
});

const ALL_KEY = 'all';

const SplitBillOptions = ([ALL_KEY, true, false] as SplitBillingFilterType[]).map(key => {
	const id = key === ALL_KEY ? 'All' : key ? 'Yes' : 'No';
	const option: ISelectOption<SplitBillingFilterType> = {
		id,
		text: id,
		dataContext: key,
	};
	return option;
});

interface IOpenInvoiceRecord {
	invoiceId: string;
	account: IAccount;
	openInvoice: IChargifyInvoice;
	customerChargifyUrl: string;
}

type SplitBillingFilterType = boolean | 'all';
type CollectionMethodFilterType = string | 'all';
type CSMFilterType = string | 'all';

function matchInvoiceRole(record: IOpenInvoiceRecord, selectedInvoiceRole: InvoiceRoleOption | null) {
	if (selectedInvoiceRole === InvoiceRoleOption.All) {
		return true;
	}

	return record.openInvoice.role.toLocaleLowerCase() === selectedInvoiceRole.toLocaleLowerCase();
}

function matchSplit(record: IOpenInvoiceRecord, selectedSplit: SplitBillingFilterType) {
	if (selectedSplit === ALL_KEY) {
		return true;
	}

	return record.openInvoice.isSplit === selectedSplit;
}

function matchCollectionMethod(record: IOpenInvoiceRecord, selectedCollectionMethod: CollectionMethodFilterType) {
	if (selectedCollectionMethod === ALL_KEY) {
		return true;
	}

	return record.openInvoice.collectionMethod === selectedCollectionMethod;
}

function matchCSM(record: IOpenInvoiceRecord, selectedCSM: CSMFilterType) {
	if (selectedCSM === ALL_KEY) {
		return true;
	}

	return record.account.additionalInfo?.customerSuccessName === selectedCSM;
}

export function OpenInvoices() {
	const [key] = React.useState(uuid());
	const history = useHistory();
	const location = useLocation();

	// Parse current URL parameters
	const searchParams = React.useMemo(() => parseQueryString(location.search), [location.search]);
	// Read from URL parameters or use defaults
	const selectedInvoiceRole = (searchParams.invoiceType as InvoiceRoleOption) || InvoiceRoleOption.All;
	const selectedSplit = React.useMemo(() => {
		const splitParam = searchParams.split;
		if (!splitParam || splitParam === ALL_KEY) return ALL_KEY;
		return splitParam === 'true' ? true : false;
	}, [searchParams.split]);
	const selectedCollectionMethod = (searchParams.collection as CollectionMethodFilterType) || ALL_KEY;
	const selectedCSM = (searchParams.csm as CSMFilterType) || ALL_KEY;
	// Helper function to update URL parameters
	const updateUrlParams = React.useCallback(
		(updates: Record<string, string | undefined>) => {
			const currentParams = parseQueryString(location.search);
			const newParams = { ...currentParams };

			// Apply updates
			Object.entries(updates).forEach(([paramKey, value]) => {
				if (value === undefined) {
					delete newParams[paramKey];
				} else {
					newParams[paramKey] = value;
				}
			});

			// Navigate to updated URL
			history.push({
				pathname: location.pathname,
				search: stringifyQueryString(newParams),
			});
		},
		[history, location.pathname, location.search]
	);

	const openInvoicesQuery = useAdminAccountOpenInvoicesQuery();
	const exportOpenInvoicesMutation = useAdminAccountExportOpenInvoicesMutation();
	const openInvoices = React.useMemo(() => {
		const isDev = Api.isDevOrTestEnvironment;
		return (
			openInvoicesQuery.data?.flatMap(page =>
				page.openInvoices.flatMap(
					openInvoice =>
						({
							invoiceId: openInvoice.invoiceId,
							account: page.account,
							openInvoice,
							customerChargifyUrl: `https://real-magic${isDev ? '-clone' : ''}.chargify.com/customers/${page.account.planDetails.billingReferenceId}`,
						}) as IOpenInvoiceRecord
				)
			) ?? []
		);
	}, [openInvoicesQuery.data]);
	// Generate dynamic filter options from the data
	const collectionMethodOptions = React.useMemo(() => {
		const methods = new Set<string>();
		openInvoices.forEach(record => {
			if (record.openInvoice.collectionMethod) {
				methods.add(record.openInvoice.collectionMethod);
			}
		});

		const options: ISelectOption<CollectionMethodFilterType>[] = [{ id: 'All', text: 'All', dataContext: ALL_KEY }];

		Array.from(methods)
			.sort()
			.forEach(method => {
				options.push({
					id: method,
					text: method,
					dataContext: method,
				});
			});
		return options;
	}, [openInvoices]);

	const csmOptions = React.useMemo(() => {
		const csms = new Set<string>();
		openInvoices.forEach(record => {
			if (record.account.additionalInfo?.customerSuccessName) {
				csms.add(record.account.additionalInfo.customerSuccessName);
			}
		});

		const options: ISelectOption<CSMFilterType>[] = [{ id: 'All', text: 'All', dataContext: ALL_KEY }];

		Array.from(csms)
			.sort()
			.forEach(csm => {
				options.push({
					id: csm,
					text: csm,
					dataContext: csm,
				});
			});
		return options;
	}, [openInvoices]);
	const openInvoicesFiltered = React.useMemo(() => {
		return openInvoices
			.filter(record => {
				return (
					matchInvoiceRole(record, selectedInvoiceRole) &&
					matchSplit(record, selectedSplit) &&
					matchCollectionMethod(record, selectedCollectionMethod) &&
					matchCSM(record, selectedCSM)
				);
			})
			.sort((a, b) => {
				return a.openInvoice.issueDate > b.openInvoice.issueDate ? 1 : -1;
			});
	}, [openInvoices, selectedInvoiceRole, selectedSplit, selectedCollectionMethod, selectedCSM]);

	const { isPending } = openInvoicesQuery;

	const noResultsFound = !isPending && openInvoicesQuery.isFetched && openInvoices.length === 0;
	const selectInvoiceRoleProperty = (selectedOption: ISelectOption<InvoiceRoleOption>) => {
		updateUrlParams({
			invoiceType: selectedOption.dataContext === InvoiceRoleOption.All ? undefined : selectedOption.dataContext,
		});
	};
	const selectSplitBillProperty = (selectedOption: ISelectOption<SplitBillingFilterType>) => {
		updateUrlParams({
			split: selectedOption.dataContext === ALL_KEY ? undefined : String(selectedOption.dataContext),
		});
	};

	const selectCollectionMethodProperty = (selectedOption: ISelectOption<CollectionMethodFilterType>) => {
		updateUrlParams({
			collection: selectedOption.dataContext === ALL_KEY ? undefined : selectedOption.dataContext,
		});
	};

	const selectCSMProperty = (selectedOption: ISelectOption<CSMFilterType>) => {
		updateUrlParams({
			csm: selectedOption.dataContext === ALL_KEY ? undefined : selectedOption.dataContext,
		});
	};

	const hasFiltersApplied = React.useMemo(() => {
		return (
			selectedInvoiceRole !== InvoiceRoleOption.All ||
			selectedSplit !== ALL_KEY ||
			selectedCollectionMethod !== ALL_KEY ||
			selectedCSM !== ALL_KEY
		);
	}, [selectedInvoiceRole, selectedSplit, selectedCollectionMethod, selectedCSM]);
	const onClearFilters = () => {
		updateUrlParams({
			invoiceType: undefined,
			split: undefined,
			collection: undefined,
			csm: undefined,
		});
	};

	const exportOpenInvoices = () => {
		exportOpenInvoicesMutation.mutate();
	};

	return (
		<div className={css(styleSheet.container)} key={key}>
			<Portal destination={MastheadPortalId}>
				<NavigationBreadcrumbsBar currentLocationName='Open Invoices' />{' '}
			</Portal>
			<header className={css(dbStyleSheet.header, styleSheet.header)}>
				<div className={css(dbStyleSheet.mediumInput)}>
					<label className={css(styleSheet.label)}>CSM:</label>
					<Select
						value={csmOptions.find(o => o.dataContext === selectedCSM)?.id ?? 'All'}
						onValueChange={id => {
							const option = csmOptions.find(o => o.id === id);
							if (option) selectCSMProperty(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<span>{csmOptions.find(o => o.dataContext === selectedCSM)?.text ?? 'All'}</span>
						</SelectTrigger>
						<SelectContent>
							{csmOptions.map(option => (
								<SelectItem key={option.id} value={option.id}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className={css(dbStyleSheet.mediumInput)}>
					<label className={css(styleSheet.label)}>Collection:</label>
					<Select
						value={collectionMethodOptions.find(o => o.dataContext === selectedCollectionMethod)?.id ?? 'All'}
						onValueChange={id => {
							const option = collectionMethodOptions.find(o => o.id === id);
							if (option) selectCollectionMethodProperty(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<span>
								{collectionMethodOptions.find(o => o.dataContext === selectedCollectionMethod)?.text ?? 'All'}
							</span>
						</SelectTrigger>
						<SelectContent>
							{collectionMethodOptions.map(option => (
								<SelectItem key={option.id} value={option.id}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className={css(dbStyleSheet.mediumInput)}>
					<label className={css(styleSheet.label)}>Invoice Type:</label>
					<Select
						value={InvoiceRoleOptions.find(o => o.dataContext === selectedInvoiceRole)?.id ?? 'All'}
						onValueChange={id => {
							const option = InvoiceRoleOptions.find(o => o.id === id);
							if (option) selectInvoiceRoleProperty(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<span>{InvoiceRoleOptions.find(o => o.dataContext === selectedInvoiceRole)?.text ?? 'All'}</span>
						</SelectTrigger>
						<SelectContent>
							{InvoiceRoleOptions.map(option => (
								<SelectItem key={option.id} value={option.id}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>{' '}
				<div className={css(dbStyleSheet.mediumInput)}>
					<label className={css(styleSheet.label)}>Split:</label>
					<Select
						value={SplitBillOptions.find(o => o.dataContext === selectedSplit)?.id ?? 'All'}
						onValueChange={id => {
							const option = SplitBillOptions.find(o => o.id === id);
							if (option) selectSplitBillProperty(option);
						}}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<span>{SplitBillOptions.find(o => o.dataContext === selectedSplit)?.text ?? 'All'}</span>
						</SelectTrigger>
						<SelectContent>
							{SplitBillOptions.map(option => (
								<SelectItem key={option.id} value={option.id}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>{' '}
				{hasFiltersApplied && (
					<div className={css(dbStyleSheet.mediumInput)}>
						<Button kind='reverse' onClick={onClearFilters}>
							Clear filters
						</Button>
					</div>
				)}
				<div className={css(dbStyleSheet.mediumInput, styleSheet.exportButton)}>
					<button
						disabled={!exportOpenInvoicesMutation.isIdle}
						onClick={exportOpenInvoices}
						className={css(styleSheet.button)}
					>
						<span>
							<ExportIcon />
							<span className={css(styleSheet.buttonText)}>Export</span>
						</span>
					</button>
				</div>
			</header>

			<div className={css(dbStyleSheet.tableContainer)}>
				<DataGrid styles={[dbStyleSheet.tableOverrides]}>
					{' '}
					<DataGridColumns>
						<DataGridColumn styles={[styleSheet.companyColumn]}>Company</DataGridColumn>
						<DataGridColumn styles={[styleSheet.csmColumn]}>CSM</DataGridColumn>
						<DataGridColumn>Issue Date</DataGridColumn>
						<DataGridColumn>Days Past Due</DataGridColumn>
						<DataGridColumn>Due Amt</DataGridColumn>
						<DataGridColumn>Paid Amt</DataGridColumn>
						<DataGridColumn>Total Amt</DataGridColumn>
						<DataGridColumn>Collection</DataGridColumn>
						<DataGridColumn>Type</DataGridColumn> <DataGridColumn>Split</DataGridColumn>
						<DataGridColumn>Memo</DataGridColumn>
						<DataGridColumn styles={[styleSheet.actionsColumn]}>Actions</DataGridColumn>
					</DataGridColumns>
					<DataGridRows>
						{!isPending
							? openInvoicesFiltered.map(record => (
									<DataGridRow key={record.invoiceId}>
										{' '}
										<DataGridRowItem styles={[baseStyleSheet.truncateText]}>
											<NavLink to={`/accounts/${record.account.id}`} className={css(styleSheet.navLink)}>
												<h6
													className={css(
														dbStyleSheet.name,
														baseStyleSheet.truncateText,
														baseStyleSheet.fontBold,
														styleSheet.companyColumn
													)}
												>
													{record.account.companyName}
												</h6>
											</NavLink>{' '}
										</DataGridRowItem>
										<DataGridRowItem styles={[baseStyleSheet.truncateText]}>
											{record.account.additionalInfo?.customerSuccessName || 'Unassigned'}
										</DataGridRowItem>
										<DataGridRowItem>
											{record.openInvoice.issueDate ? moment(record.openInvoice.issueDate).format('MM/DD/YYYY') : '-'}
										</DataGridRowItem>
										<DataGridRowItem>
											{record.openInvoice.dueDate ? moment().diff(moment(record.openInvoice.issueDate), 'days') : '-'}
										</DataGridRowItem>
										<DataGridRowItem styles={[dbStyleSheet.amountText]}>
											{record.openInvoice.dueAmount
												? `${numberToUSDCurrencyStringValue(record.openInvoice.dueAmount)}`
												: '-'}
										</DataGridRowItem>
										<DataGridRowItem styles={[dbStyleSheet.amountText]}>
											{record.openInvoice.paidAmount
												? `${numberToUSDCurrencyStringValue(record.openInvoice.paidAmount)}`
												: '-'}
										</DataGridRowItem>
										<DataGridRowItem styles={[dbStyleSheet.amountText]}>
											{record.openInvoice.totalAmount
												? `${numberToUSDCurrencyStringValue(record.openInvoice.totalAmount)}`
												: '-'}
										</DataGridRowItem>{' '}
										<DataGridRowItem>{record.openInvoice.collectionMethod}</DataGridRowItem>
										<DataGridRowItem>{record.openInvoice.role}</DataGridRowItem>
										<DataGridRowItem>{record.openInvoice.isSplit ? 'Y' : '-'}</DataGridRowItem>
										<DataGridRowItem styles={[baseStyleSheet.truncateText]}>{record.openInvoice.memo}</DataGridRowItem>
										<DataGridRowItem>
											<a href={record.openInvoice.publicUrl} target='_blank' rel='noopener noreferrer'>
												Invoice
											</a>
											&nbsp;|&nbsp;
											<a href={record.customerChargifyUrl} target='_blank' rel='noopener noreferrer'>
												Customer
											</a>
										</DataGridRowItem>
									</DataGridRow>
								))
							: null}

						{isPending && (
							<DataGridRow>
								<DataGridRowItem fullColSpan>
									<LoadingSpinner type='large' />
								</DataGridRowItem>
							</DataGridRow>
						)}
					</DataGridRows>
				</DataGrid>

				{noResultsFound ? (
					<div className={css(dbStyleSheet.noResults)}>
						<EmptySearchIcon />

						<span>We did not find anyone matching your search criteria.</span>
						<span>Please clear your filters and try again.</span>
					</div>
				) : null}
			</div>
			<ExportConfirmationModalV2
				mutationResult={exportOpenInvoicesMutation}
				modalProps={{
					isOpen: !exportOpenInvoicesMutation.isIdle,
					onRequestClose: () => exportOpenInvoicesMutation.reset(),
				}}
			/>
		</div>
	);
}
